export interface KroKrgIntersectionResult {
  points: Array<{ sg: number; kro: number; krg: number }>;
  intersectionSg: number | null;
  intersectionK: number | null;
  status: 'valid' | 'invalid-input' | 'no-intersection';
  invalidRange: boolean;
}

interface KroKrgInputs {
  sgValues: Array<number | null | undefined>;
  swi: number | null | undefined;
  sgc: number | null | undefined;
  sor: number | null | undefined;
  no: number | null | undefined;
  ng: number | null | undefined;
  krgEndPoint: number | null | undefined;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const calculateKroKrgIntersection = (
  inputs: KroKrgInputs
): KroKrgIntersectionResult => {
  const { sgValues, swi, sgc, sor, no, ng, krgEndPoint } = inputs;

  if (
    !isFiniteNumber(swi) ||
    !isFiniteNumber(sgc) ||
    !isFiniteNumber(sor) ||
    !isFiniteNumber(no) ||
    !isFiniteNumber(ng) ||
    !isFiniteNumber(krgEndPoint)
  ) {
    return {
      points: [],
      intersectionSg: null,
      intersectionK: null,
      status: 'invalid-input',
      invalidRange: false
    };
  }

  const denom = 1 - swi - sgc - sor;
  if (denom <= 0) {
    return {
      points: [],
      intersectionSg: null,
      intersectionK: null,
      status: 'invalid-input',
      invalidRange: false
    };
  }

  const points: Array<{ sg: number; kro: number; krg: number }> = [];
  let invalidRange = false;

  (sgValues ?? []).forEach(sgValue => {
    if (!isFiniteNumber(sgValue)) {
      return;
    }

    if (sgValue < sgc || sgValue > 1 - swi - sor) {
      invalidRange = true;
      return;
    }

    const sgn = (sgValue - sgc) / denom;
    const son = (1 - sgValue - sor) / denom;

    if (sgn < 0 || son < 0) {
      return;
    }

    const kro = Math.pow(son, no);
    const krg = krgEndPoint * Math.pow(sgn, ng);

    if (!Number.isFinite(kro) || !Number.isFinite(krg)) {
      return;
    }

    points.push({ sg: sgValue, kro, krg });
  });

  if (points.length === 0) {
    return {
      points,
      intersectionSg: null,
      intersectionK: null,
      status: 'no-intersection',
      invalidRange
    };
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const diffCurrent = current.kro - current.krg;
    const diffNext = next.kro - next.krg;

    if (diffCurrent === 0) {
      return {
        points,
        intersectionSg: current.sg,
        intersectionK: current.kro,
        status: 'valid',
        invalidRange
      };
    }

    if (diffCurrent * diffNext < 0 || diffNext === 0) {
      const divisor = diffCurrent - diffNext;
      const t = divisor === 0 ? 0 : diffCurrent / divisor;
      const intersectionSg = current.sg + t * (next.sg - current.sg);
      const intersectionK = current.kro + t * (next.kro - current.kro);

      return {
        points,
        intersectionSg,
        intersectionK,
        status: 'valid',
        invalidRange
      };
    }
  }

  return {
    points,
    intersectionSg: null,
    intersectionK: null,
    status: 'no-intersection',
    invalidRange
  };
};
